import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Calendar, Clock, X, Plus, Repeat, CalendarDays } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Input } from './ui/input';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from './ui/select';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
// Every 60-min hour from 07:00 → 21:00 — internal calendar still uses 30-min ticks
// but every class booking is 60 minutes so we only offer hour starts.
const HOURS = Array.from({ length: 15 }, (_, i) => `${String(7 + i).padStart(2, '0')}:00`);

export default function TeacherAvailability() {
  const [slots, setSlots] = useState([]);
  const [tz, setTz] = useState('Asia/Calcutta');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Recurring composer
  const [recDay, setRecDay] = useState('1');
  const [recTime, setRecTime] = useState('16:00');
  // Onetime composer
  const [otDate, setOtDate] = useState(new Date().toISOString().slice(0, 10));
  const [otTime, setOtTime] = useState('16:00');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/scheduler/availability/me');
      setSlots(res.data.slots || []);
      setTz(res.data.tz || 'Asia/Calcutta');
    } catch (e) { toast.error('Failed to load availability'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const addRecurring = async () => {
    setSaving(true);
    try {
      await api.post('/scheduler/availability', {
        kind: 'recurring', day_of_week: parseInt(recDay, 10), start_time: recTime,
      });
      toast.success(`Added ${DAYS[parseInt(recDay, 10)]} · ${recTime}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to add'); }
    finally { setSaving(false); }
  };
  const addOnetime = async () => {
    setSaving(true);
    try {
      await api.post('/scheduler/availability', { kind: 'onetime', date: otDate, start_time: otTime });
      toast.success(`Added ${otDate} · ${otTime}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to add'); }
    finally { setSaving(false); }
  };
  const removeSlot = async (id) => {
    if (!window.confirm('Remove this availability slot?')) return;
    try {
      await api.delete(`/scheduler/availability/${id}`);
      toast.success('Removed');
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to remove'); }
  };

  const recurring = slots.filter(s => s.kind === 'recurring');
  const onetime = slots.filter(s => s.kind === 'onetime');

  return (
    <div className="space-y-4" data-testid="teacher-availability">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-[#0F172A]">Availability</h2>
        <span className="text-xs text-slate-500">Timezone: <b>{tz}</b> · All classes are 60 min</span>
      </div>

      <Tabs defaultValue="recurring" className="w-full">
        <TabsList className="grid grid-cols-2 w-full max-w-md">
          <TabsTrigger value="recurring" data-testid="tab-recurring"><Repeat className="w-3.5 h-3.5 mr-1.5" /> Long Term ({recurring.length})</TabsTrigger>
          <TabsTrigger value="onetime" data-testid="tab-onetime"><CalendarDays className="w-3.5 h-3.5 mr-1.5" /> One Time ({onetime.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="recurring" className="space-y-4">
          <div className="nb-card p-4">
            <p className="text-xs font-bold text-slate-600 mb-3">Add recurring weekly slot</p>
            <div className="flex items-end gap-2 flex-wrap">
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Day</label>
                <Select value={recDay} onValueChange={setRecDay}>
                  <SelectTrigger className="w-32" data-testid="rec-day"><SelectValue /></SelectTrigger>
                  <SelectContent>{DAYS.map((d, i) => <SelectItem key={i} value={String(i)}>{d}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Start time (60 min)</label>
                <Select value={recTime} onValueChange={setRecTime}>
                  <SelectTrigger className="w-32" data-testid="rec-time"><SelectValue /></SelectTrigger>
                  <SelectContent>{HOURS.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Button onClick={addRecurring} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="rec-add-btn">
                <Plus className="w-4 h-4 mr-1" /> Add
              </Button>
            </div>
          </div>

          {loading ? <p className="text-sm text-slate-400">Loading…</p> : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              {recurring.length === 0 && <p className="col-span-full text-sm text-slate-400">No recurring slots yet.</p>}
              {recurring.map(s => (
                <div key={s.id} className="nb-card p-3 flex items-center justify-between" data-testid={`rec-slot-${s.id}`}>
                  <div>
                    <p className="font-bold text-sm text-slate-900 flex items-center gap-1.5"><Repeat className="w-3 h-3 text-indigo-600" /> Every {DAYS[s.day_of_week]}</p>
                    <p className="text-xs text-slate-500">{s.start_time} — {addHour(s.start_time)}</p>
                  </div>
                  <button onClick={() => removeSlot(s.id)} className="text-slate-400 hover:text-red-500" title="Remove"><X className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="onetime" className="space-y-4">
          <div className="nb-card p-4">
            <p className="text-xs font-bold text-slate-600 mb-3">Add one-time slot</p>
            <div className="flex items-end gap-2 flex-wrap">
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Date</label>
                <Input type="date" value={otDate} onChange={e => setOtDate(e.target.value)} className="w-40" data-testid="ot-date" />
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Start time</label>
                <Select value={otTime} onValueChange={setOtTime}>
                  <SelectTrigger className="w-32" data-testid="ot-time"><SelectValue /></SelectTrigger>
                  <SelectContent>{HOURS.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Button onClick={addOnetime} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="ot-add-btn">
                <Plus className="w-4 h-4 mr-1" /> Add
              </Button>
            </div>
          </div>

          {loading ? <p className="text-sm text-slate-400">Loading…</p> : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              {onetime.length === 0 && <p className="col-span-full text-sm text-slate-400">No one-time slots yet.</p>}
              {onetime.map(s => (
                <div key={s.id} className="nb-card p-3 flex items-center justify-between" data-testid={`ot-slot-${s.id}`}>
                  <div>
                    <p className="font-bold text-sm text-slate-900 flex items-center gap-1.5"><Calendar className="w-3 h-3 text-emerald-600" /> {s.date}</p>
                    <p className="text-xs text-slate-500">{s.start_time} — {addHour(s.start_time)}</p>
                  </div>
                  <button onClick={() => removeSlot(s.id)} className="text-slate-400 hover:text-red-500" title="Remove"><X className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function addHour(hhmm) {
  const [h, m] = hhmm.split(':').map(Number);
  return `${String((h + 1) % 24).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}
